/**
 * Norvu cart: one-time to subscription upgrade pill (v1)
 *
 * Adds a pill under every one-time line in the cart drawer offering to convert
 * it to a monthly subscription. Deliberately standalone: it does not modify
 * cart.js. It hooks in through three public seams only:
 *
 *   1. A MutationObserver on [data-cart-body], because renderCart() rebuilds
 *      that subtree wholesale on every render, so injected nodes never survive.
 *   2. A delegated click listener on document. cart.js binds its own document
 *      handler, but the pill matches none of its selectors and sits outside the
 *      [data-cart-link] anchors, so the two never collide.
 *   3. window.NorvuCart.{changeLine,add,refresh} for the actual mutation. Those
 *      all run inside cart.js's FIFO enqueue(), so the remove-then-add sequence
 *      is serialized against any other in-flight cart operation.
 *
 * Styles are injected here rather than living in dev-overrides.css so the whole
 * feature is one file to add or remove.
 *
 * Upgrade path is remove-then-add, not an in-place edit: Shopify has no endpoint
 * that adds a selling_plan to an existing line. /cart/change.js only moves
 * quantity. Going through changeLine(...,0) then add(...,plan) reuses the group
 * logic in cart.js that already handles Shopify splitting subscription lines
 * across multiple line items when a discount allocates unevenly.
 */
(function () {
  'use strict';

  // Store-wide "Autoship and Save" monthly plan. Used when the product's own
  // plan list contains it; otherwise we fall back to matching by name so the
  // pill still works if a product sits in a different plan group.
  var PINNED_MONTHLY_PLAN = '692955611438';

  var planCache = {}; // handle -> planId | null

  function drawer() { return document.querySelector('[data-cart-drawer]'); }

  function discountPct() {
    var d = drawer();
    var v = d && parseInt(d.getAttribute('data-autoship-discount'), 10);
    return (v && v > 0) ? v : 20;
  }

  function labelText() {
    return 'Upgrade to Subscription - Save ' + discountPct() + '%';
  }

  /* ---------- styles ---------- */
  function injectStyles() {
    if (document.getElementById('norvu-upgrade-css')) return;
    var s = document.createElement('style');
    s.id = 'norvu-upgrade-css';
    s.textContent = [
      '.norvu-cart__upgrade{display:inline-flex;align-items:center;gap:7px;margin-top:10px;padding:7px 13px;',
      'border:1px solid #16182d;border-radius:100px;background:#fff;color:#16182d;font-family:inherit;',
      'font-size:12px;font-weight:600;line-height:1.3;cursor:pointer;white-space:nowrap;',
      'max-width:100%;-webkit-tap-highlight-color:transparent;transition:background .15s ease,color .15s ease,opacity .15s ease}',
      '.norvu-cart__upgrade:hover:not([disabled]){background:#16182d;color:#fff}',
      '.norvu-cart__upgrade:hover:not([disabled]) .norvu-cart__upgrade-dot{background:#fff}',
      '.norvu-cart__upgrade:focus-visible{outline:2px solid #16182d;outline-offset:2px}',
      '.norvu-cart__upgrade[disabled]{opacity:.65;cursor:default}',
      '.norvu-cart__upgrade-dot{width:6px;height:6px;border-radius:50%;background:#6ccf75;flex:0 0 auto;',
      'transition:background .15s ease}',
      '.norvu-cart__upgrade.is-busy .norvu-cart__upgrade-dot{animation:norvu-upgrade-pulse .9s ease-in-out infinite}',
      '@keyframes norvu-upgrade-pulse{0%,100%{opacity:1}50%{opacity:.2}}',
      // Label must never wrap. Below 420px the pill goes full-row and the type
      // scales down instead, which buys enough width to keep it on one line.
      '@media (max-width:420px){.norvu-cart__upgrade{width:100%;justify-content:center;font-size:11px;padding:7px 10px;gap:6px}}',
      '@media (max-width:340px){.norvu-cart__upgrade{font-size:10px;letter-spacing:-.01em;padding:7px 8px}}'
    ].join('');
    document.head.appendChild(s);
  }

  /* ---------- helpers ---------- */
  function handleOf(row) {
    var link = row.querySelector('a[href*="/products/"]');
    if (!link) return '';
    var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function qtyOf(row) {
    var el = row.querySelector('.norvu-cart__qty-value');
    return parseInt(el && el.textContent, 10) || 1;
  }

  function setLabel(btn, text) {
    var el = btn.querySelector('[data-upgrade-label]');
    if (el) el.textContent = text;
  }

  function setBusy(btn, busy, text) {
    btn.disabled = busy;
    btn.classList.toggle('is-busy', busy);
    if (text) setLabel(btn, text);
  }

  /**
   * Resolve the monthly selling plan for a product. Prefers the pinned store
   * plan when the product carries it, else the first plan whose name reads as
   * monthly. Names like "Deliver every 3 months" are excluded so a quarterly
   * plan never gets mistaken for the monthly one.
   */
  function resolveMonthlyPlan(handle) {
    if (!handle) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(planCache, handle)) {
      return Promise.resolve(planCache[handle]);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var plans = [];
        ((p && p.selling_plan_groups) || []).forEach(function (g) {
          (g.selling_plans || []).forEach(function (plan) { plans.push(plan); });
        });

        var found = null;
        for (var i = 0; i < plans.length; i++) {
          if (String(plans[i].id) === PINNED_MONTHLY_PLAN) { found = PINNED_MONTHLY_PLAN; break; }
        }
        if (!found) {
          for (var j = 0; j < plans.length; j++) {
            var n = String(plans[j].name || '').toLowerCase();
            if (n.indexOf('month') === -1) continue;
            if (/\b([2-9]|\d{2,})\s*-?\s*months?/.test(n)) continue;
            found = String(plans[j].id);
            break;
          }
        }
        planCache[handle] = found;
        return found;
      })
      .catch(function () { planCache[handle] = null; return null; });
  }

  /* ---------- upgrade ---------- */
  function upgrade(row, btn) {
    var variant = row.getAttribute('data-variant');
    if (!variant || !window.NorvuCart) return;

    var qty = qtyOf(row);
    var handle = handleOf(row);

    setBusy(btn, true, 'Upgrading\u2026');

    resolveMonthlyPlan(handle)
      .then(function (planId) {
        if (!planId) throw new Error('No monthly selling plan for ' + handle);
        // Drop the one-time group first, then re-add the same quantity on the
        // subscription plan. Both calls are enqueued by cart.js, so they cannot
        // interleave with each other or with a qty click.
        return window.NorvuCart.changeLine(variant, null, false, 0)
          .then(function () { return window.NorvuCart.add(variant, planId, qty); });
      })
      .then(function () {
        // refresh() re-renders the drawer. The line is now a subscription, so
        // sync() will not re-attach a pill to it.
        return window.NorvuCart.refresh();
      })
      .catch(function (err) {
        console.warn('[NorvuUpgrade] upgrade failed:', err);
        if (!btn.isConnected) return;
        setBusy(btn, false, 'Try again');
        setTimeout(function () {
          if (btn.isConnected) setLabel(btn, labelText());
        }, 1800);
      });
  }

  /* ---------- injection ---------- */
  function buildPill() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'norvu-cart__upgrade';
    b.setAttribute('data-cart-upgrade', '');
    var dot = document.createElement('span');
    dot.className = 'norvu-cart__upgrade-dot';
    dot.setAttribute('aria-hidden', 'true');
    var lab = document.createElement('span');
    lab.setAttribute('data-upgrade-label', '');
    lab.textContent = labelText();
    b.appendChild(dot);
    b.appendChild(lab);
    return b;
  }

  function sync() {
    var body = document.querySelector('[data-cart-body]');
    if (!body) return;
    // One-time lines only: empty data-plan, and not a free gift line (gifts also
    // have no plan but must never be upgraded).
    var rows = body.querySelectorAll('.norvu-cart__item[data-plan=""][data-is-gift="0"]');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.querySelector('[data-cart-upgrade]')) return;
      var host = row.querySelector('.norvu-cart__item-body') || row;
      var pill = buildPill();
      host.appendChild(pill);
      // Verify the product actually offers a subscription; drop the pill if not
      // (bundles and accessories may be one-time only).
      resolveMonthlyPlan(handleOf(row)).then(function (planId) {
        if (!planId && pill.parentNode) pill.parentNode.removeChild(pill);
      });
    });
  }

  /* ---------- init ---------- */
  function init() {
    injectStyles();

    document.addEventListener('click', function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;
      var btn = e.target.closest('[data-cart-upgrade]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var row = btn.closest('.norvu-cart__item');
      if (row) upgrade(row, btn);
    });

    var body = document.querySelector('[data-cart-body]');
    if (body) {
      // renderCart() replaces body.innerHTML on every render, so re-inject after
      // each mutation. sync() is idempotent, so the mutation it causes settles
      // after one extra pass.
      new MutationObserver(function () { sync(); })
        .observe(body, { childList: true, subtree: true });
      sync();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
